/**
 * The live log of one pipeline item. Against the hub: POST /api/logs/pipelineItem:<id> opens a
 * stream whose chunks arrive as SSE `log` events into the store. In demo mode a generator
 * writes plausible lines into the same store slot, so the viewer behaves identically.
 */
import { useEffect, useState } from 'react';
import type { Pipeline, PipelineItem } from '@orgus/protocol';
import { formatDecimal } from '@orgus/ui';
import { useOrgus, useLog } from '../../lib/store.ts';
import { useLogStream } from '../../lib/useLogStream.ts';
import { failedLabel } from './present.ts';

export interface ItemLog {
  text: string;
  live: boolean;
  loading: boolean;
  error: unknown;
  retry: () => void;
}

const sleepLines = (min: number, max: number) => min + Math.random() * (max - min);

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

/** Lines a stage would print, for the demo. Generic: built from the stage titles. */
function stageLines(stageTitle: string, item: PipelineItem, n: number): string[] {
  const t = stageTitle.toLowerCase();
  const h = hash(item.id + stageTitle);
  if (t.includes('queue')) return [`Queued ${item.title}`, `Submitted by ${item.by ?? 'unknown'}${item.detail ? `, ${item.detail}` : ''}`];
  if (t.includes('verif') || t.includes('test') || t.includes('check')) {
    return [
      'Resolving changed packages from the diff',
      `Typecheck: ${3 + (h % 5)} packages`,
      `Typecheck passed in ${formatDecimal(8 + (h % 9))} s`,
      'Lint passed',
      `Unit tests: ${180 + (h % 260)} passed in ${formatDecimal(20 + (h % 30))} s`,
      `Scope check passed`,
      ...Array.from({ length: n }, (_, i) => `  spec ${i + 1}: ok`),
    ];
  }
  if (t.includes('push')) return ['Rebasing onto origin/main', 'Pushing to origin main', `main moved to ${(h >>> 4).toString(16).slice(0, 7)}`];
  if (t.includes('deploy')) return ['Deploying', 'Build output ready', 'Upload finished', 'Health check passed'];
  if (t.includes('land') || t.includes('done')) return ['Landed'];
  return [`${stageTitle}: working`, `${stageTitle}: done`];
}

function fullLog(p: Pipeline, item: PipelineItem): string[] {
  const order = p.stages.map((s) => s.id);
  const upto = Math.max(0, order.indexOf(item.stage));
  const out: string[] = [];
  p.stages.slice(0, upto + 1).forEach((s, i) => {
    out.push(`== ${s.title} ==`);
    const lines = stageLines(s.title, item, 3);
    const lastStage = i === upto;
    if (lastStage && item.state === 'failed') {
      out.push(...lines.slice(0, Math.max(1, lines.length - 3)));
      out.push(`error: ${item.detail ?? 'the stage failed'}`);
      out.push(failedLabel(s.title));
    } else if (lastStage && item.state === 'cancelled') {
      out.push(lines[0] ?? s.title, 'Cancelled before it started work');
    } else if (lastStage && item.state === 'active') {
      out.push(...lines.slice(0, Math.ceil(lines.length / 2)));
    } else {
      out.push(...lines);
    }
  });
  if (item.state === 'passed') out.push(`Passed${item.detail ? `: ${item.detail}` : ''}`);
  return out;
}

/** Writes a demo log into the store; returns a stop function. */
function startMockLog(streamId: string, p: Pipeline, item: PipelineItem): () => void {
  const store = useOrgus.getState();
  store.clearLog(streamId);
  store.trackLog(streamId);
  const lines = fullLog(p, item);
  const live = item.state === 'active' || item.state === 'queued';
  let i = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;
  const burst = live ? Math.max(2, lines.length - 2) : lines.length;
  // Most of it arrives at once (the backlog), the rest streams.
  timer = setTimeout(function tick() {
    if (stopped) return;
    const take = i === 0 ? burst : 1;
    const chunk = lines.slice(i, i + take);
    i += take;
    if (chunk.length) {
      useOrgus.getState().appendLog({ streamId, ref: { type: 'pipelineItem', id: item.id }, text: `${chunk.join('\n')}\n`, end: !live && i >= lines.length });
    } else if (live) {
      const n = Math.floor(Math.random() * 400) + 1;
      useOrgus.getState().appendLog({ streamId, ref: { type: 'pipelineItem', id: item.id }, text: `  spec ${n}: ok (${formatDecimal(Math.random() * 2, 2)} s)\n` });
    }
    if (!live && i >= lines.length) return;
    timer = setTimeout(tick, sleepLines(450, 1100));
  }, 280);
  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
  };
}

/** Open (and close) the log stream for `item` while `enabled`. */
export function useItemLog(pipeline: Pipeline | undefined, item: PipelineItem | undefined, enabled: boolean): ItemLog {
  const mode = useOrgus((s) => s.mode);
  const itemId = item?.id;
  const canLog = !!item?.logRef;
  // Live: the hub stream, opened and closed by useLogStream.
  const live = useLogStream(itemId && canLog ? { type: 'pipelineItem', id: itemId } : null, enabled && !!pipeline && mode === 'live');

  // Demo: generated lines written into the same store slot.
  const [mockId, setMockId] = useState<string | null>(null);
  const mockLog = useLog(mockId);
  // Demo logs are generated, so they are rewritten when the item finishes; a real stream just continues.
  const mockState = mode === 'mock' ? item?.state : undefined;
  useEffect(() => {
    if (mode !== 'mock' || !enabled || !pipeline || !item || !canLog) return;
    const id = `mock-log:${item.id}`;
    setMockId(id);
    const stop = startMockLog(id, pipeline, item);
    return () => {
      stop();
      useOrgus.getState().clearLog(id);
      setMockId(null);
    };
    // The stream follows the item's identity, not every patch to it.
  }, [enabled, itemId, canLog, mode, mockState]);

  const finished = item ? item.state === 'passed' || item.state === 'failed' || item.state === 'cancelled' : true;
  const streamId = mode === 'mock' ? mockId : live.streamId;
  const log = mode === 'mock' ? mockLog : live.log;
  return {
    text: log?.text ?? '',
    live: !!streamId && !log?.ended && !finished,
    loading: mode === 'mock' ? false : live.loading,
    error: mode === 'mock' ? null : live.error,
    retry: live.retry,
  };
}
